import React, { useEffect, useState } from 'react'
import { Text, Flex, Link } from 'theme-ui'
import { navigate } from 'gatsby'
import { useLocation } from '@reach/router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPhone,
  faLocationArrow,
  faCalendar,
  faUtensils,
  faTruck,
} from '@fortawesome/free-solid-svg-icons'
import checkIfExternalLink from '../../../../helpers/checkIfExternalLink'

export default function CTAWidget({
  businessData,
  configData,
  variantName,
  locationIndex,
}) {
  // === Destructing Props ===

  const { name, phone, street, city, state, zip } = businessData
  const {
    containerStyles,
    onlineOrderLinks,
    onlineOrderCTAName,
    onlineOrderClassName,
    reservationLinks,
    reservationCTAName,
    reservationClassName,
    deliveryLinks,
    deliveryCTAName,
    deliveryClassName,
    directionsHidden,
    phoneHidden,
    onlineOrderSectionAnchorPath,
    callSectionAnchorPath,
    directionSectionAnchorPath,
    reservationSectionAnchorPath,
    deliverySectionAnchorPath,
  } = configData.ctaWidget.options

  //  === State

  const [
    currentPath,
    // setCurrentPath
  ] = useState(useLocation().pathname)

  //  ====== Functions ====

  // == path functions
  const pageURL = anchorPath => anchorPath.split('#')[0]
  const sectionID = anchorPath =>
    anchorPath.split('#')[anchorPath.split('#').length - 1]

  const navigateAndScroll = (e, anchorPath) => {
    e.preventDefault()

    // if on the same page scroll to it.
    if (currentPath === pageURL(anchorPath)) {
      const targetSection = document.getElementById(`${sectionID(anchorPath)}`)
      if (
        targetSection !== null &&
        targetSection !== undefined &&
        window !== undefined
      ) {
        targetSection.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    } else {
      // if not on same page navigate to page and scroll
      navigate(pageURL(anchorPath))

      // once that page has loaded then scroll to section
      setTimeout(() => {
        const targetSection = document.getElementById(
          `${sectionID(anchorPath)}`
        )

        if (targetSection !== null && targetSection !== undefined) {
          targetSection.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }, 1500)
    }
  }

  //  === MOUNTING ==

  useEffect(() => {
    const onlineOrderCTA = document.getElementById('online-order-cta')
    const callCTA = document.getElementById('call-cta')
    const directionsCTA = document.getElementById('directions-cta')
    const reservationsCTA = document.getElementById('reservations-cta')
    const deliveryCTA = document.getElementById('delivery-cta')

    const CTAS = [
      onlineOrderCTA,
      callCTA,
      directionsCTA,
      reservationsCTA,
      deliveryCTA,
    ]
    const anchorPaths = [
      onlineOrderSectionAnchorPath,
      callSectionAnchorPath,
      directionSectionAnchorPath,
      reservationSectionAnchorPath,
      deliverySectionAnchorPath,
    ]
    anchorPaths.forEach((anchorPath, index) => {
      if (anchorPath) {
        CTAS[index].addEventListener('click', e =>
          navigateAndScroll(e, anchorPath)
        )
      }
    })
    return () => {
      anchorPaths.forEach((anchorPath, index) => {
        if (anchorPath) {
          CTAS[index].removeEventListener('click', e =>
            navigateAndScroll(e, anchorPath)
          )
        }
      })
    }
  })

  // ===== Data Manipulation
  const googleString = `https://www.google.com/maps/dir/?api=1&destination=${name}+${street}+${city}+${state}+${zip}`

  const variant = variantName ? variantName : 'ctaWidgetV1'

  const styles = containerStyles ? JSON.parse(containerStyles) : {}

  return (
    <Flex
      variant={`${variant}.container`}
      sx={styles}
      className='ctaWidgetContainer'>
      {/* ================================== */}
      {/* =========  Call CTA    =========== */}
      {/* ================================== */}
      {!phoneHidden && (
        <Link
          id='call-cta'
          href={!callSectionAnchorPath ? `tel:${phone}` : ''}
          target='_blank'
          rel='noreferrer'>
          <FontAwesomeIcon icon={faPhone} />
          <Text variant={`${variant}.ctaLabel`} as='span'>
            Call
          </Text>
        </Link>
      )}
      {/* ======================================== */}
      {/* =========  Directions CTA    =========== */}
      {/* ======================================== */}
      {!directionsHidden && (
        <Link
          id='directions-cta'
          // if there is a given google map link we go to then use that.
          // if not then check if there is anchor to go to,
          // if not use default and parse the address into a google map string
          href={
            configData?.alternativeGoogleMapLink &&
            configData?.alternativeGoogleMapLink[locationIndex]
              ? configData.alternativeGoogleMapLink
              : !directionSectionAnchorPath
              ? googleString
              : ''
          }
          target='_blank'
          rel='noreferrer'>
          <FontAwesomeIcon icon={faLocationArrow} />
          <Text variant={`${variant}.ctaLabel`} as='span'>
            Directions
          </Text>
        </Link>
      )}
      {/* ========================================== */}
      {/* =========  ONLINE ORDER CTA    =========== */}
      {/* ========================================== */}
      {(onlineOrderLinks && onlineOrderLinks[locationIndex]) ||
      onlineOrderSectionAnchorPath ? (
        <Link
          id='online-order-cta'
          className={
            onlineOrderClassName ? onlineOrderClassName[locationIndex] : ''
          }
          href={
            !onlineOrderSectionAnchorPath ? onlineOrderLinks[locationIndex] : ''
          }
          target='_blank'
          rel='noreferrer'>
          <FontAwesomeIcon icon={faUtensils} />
          <Text variant={`${variant}.ctaLabel`} as='span'>
            {onlineOrderCTAName
              ? onlineOrderCTAName[locationIndex]
              : 'Order Online'}
          </Text>
        </Link>
      ) : (
        ''
      )}
      {/* ========================================== */}
      {/* =========  Reservations CTA    =========== */}
      {/* ========================================== */}
      {(reservationLinks && reservationLinks[locationIndex]) ||
      reservationSectionAnchorPath ? (
        <Link
          id='reservations-cta'
          className={
            reservationClassName ? reservationClassName[locationIndex] : ''
          }
          href={
            !reservationSectionAnchorPath ? reservationLinks[locationIndex] : ''
          }
          target={`${
            reservationLinks &&
            checkIfExternalLink(reservationLinks[locationIndex])
              ? '_blank'
              : ''
          }`}
          rel={`${
            reservationLinks &&
            checkIfExternalLink(reservationLinks[locationIndex])
              ? 'noreferrer'
              : ''
          }`}>
          <FontAwesomeIcon icon={faCalendar} />
          <Text variant={`${variant}.ctaLabel`} as='span'>
            {reservationCTAName
              ? reservationCTAName[locationIndex]
              : 'Reservations'}
          </Text>
        </Link>
      ) : (
        ''
      )}
      {/* ====================================== */}
      {/* =========  Delivery CTA    =========== */}
      {/* ====================================== */}
      {(deliveryLinks && deliveryLinks[locationIndex]) ||
      deliverySectionAnchorPath ? (
        <Link
          id='delivery-cta'
          className={deliveryClassName ? deliveryClassName[locationIndex] : ''}
          href={!deliverySectionAnchorPath ? deliveryLinks[locationIndex] : ''}
          target='_blank'
          rel='noreferrer'>
          <FontAwesomeIcon icon={faTruck} />
          <Text variant={`${variant}.ctaLabel`} as='span'>
            {deliveryCTAName ? deliveryCTAName[locationIndex] : 'Delivery'}
          </Text>
        </Link>
      ) : (
        ''
      )}
    </Flex>
  )
}
